<template>
  <div class="monetary-panel overflow-hidden border border-gray-200 rounded-lg bg-white">
    <!-- Title bar -->
    <div class="monetary-panel__titlebar flex items-center justify-between gap-3 px-4 py-2.5">
      <h3 class="text-sm font-bold text-white uppercase tracking-wide m-0">
        {{ $t('resource_executions_weekly_title') }}
      </h3>
      <div class="flex items-center gap-2 flex-shrink-0">
        <div class="monetary-panel__switch inline-flex rounded overflow-hidden border border-white border-opacity-40">
          <button
            type="button"
            class="monetary-panel__switch-btn"
            :class="{ 'is-active': progressMode === 'inspected' }"
            @click="progressMode = 'inspected'"
          >
            {{ $t('inspected') }}
          </button>
          <button
            type="button"
            class="monetary-panel__switch-btn"
            :class="{ 'is-active': progressMode === 'actual' }"
            @click="progressMode = 'actual'"
          >
            {{ $t('actual') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Sub-header: project badge + cutoff date -->
    <div class="flex items-center justify-between gap-3 px-4 py-2 bg-white border-b border-gray-200">
      <span class="monetary-panel__project-badge">
        {{ projectName || '—' }}
      </span>
      <div class="monetary-panel__cutoff inline-flex items-center gap-2 text-xs">
        <span class="uppercase tracking-wide text-grey-darker font-semibold">
          {{ $t('report_measurement_cutoff_date') }}:
        </span>
        <span class="monetary-panel__cutoff-date">
          {{ cutoffLabel }}
        </span>
      </div>
    </div>

    <!-- Table -->
    <div class="relative">
      <div v-if="isTableLoading" class="absolute inset-0 z-10 flex items-center justify-center bg-white bg-opacity-75">
        <scale-loader />
      </div>
      <div class="overflow-x-auto">
        <table class="monetary-table w-full min-w-[1100px] text-sm">
          <thead>
            <tr class="text-[0.6875rem] uppercase tracking-wide">
              <th rowspan="2" class="monetary-table__sticky-col monetary-table__header px-3 py-2 font-bold text-left whitespace-nowrap">
                {{ $t('resource') }}
              </th>
              <th rowspan="2" class="monetary-table__header px-2 py-2 font-bold text-center whitespace-nowrap">
                {{ $t('unit') }}
              </th>
              <th colspan="6" class="monetary-table__section-header monetary-table__section-header--prev px-3 py-2 font-bold text-center whitespace-nowrap">
                {{ $t('plan') }}
              </th>
              <th colspan="6" class="monetary-table__section-header monetary-table__section-header--month px-3 py-2 font-bold text-center whitespace-nowrap">
                {{ $t('current') }}
              </th>
            </tr>

            <tr class="text-[0.625rem] uppercase tracking-wide">
              <th class="monetary-table__subheader resource-table__colheader px-2 py-1.5 font-semibold">{{ $t('total_quantity') }}</th>
              <th class="monetary-table__subheader resource-table__colheader px-2 py-1.5 font-semibold">{{ $t('accum_last_period') }}</th>
              <th class="monetary-table__subheader monetary-table__subheader--executed resource-table__colheader px-2 py-1.5 font-semibold">{{ $t('executed_in_period') }}</th>
              <th class="monetary-table__subheader monetary-table__subheader--executed resource-table__colheader px-2 py-1.5 font-semibold">{{ $t('executed_accum') }}</th>
              <th class="monetary-table__subheader resource-table__colheader px-2 py-1.5 font-semibold">{{ $t('progress') }}</th>
              <th class="monetary-table__subheader monetary-table__subheader--pending resource-table__colheader px-2 py-1.5 font-semibold">{{ $t('remaining') }}</th>

              <th class="monetary-table__subheader resource-table__colheader px-2 py-1.5 font-semibold">{{ $t('total_quantity') }}</th>
              <th class="monetary-table__subheader resource-table__colheader px-2 py-1.5 font-semibold">{{ $t('accum_last_period') }}</th>
              <th class="monetary-table__subheader monetary-table__subheader--executed resource-table__colheader px-2 py-1.5 font-semibold">{{ $t('executed_in_period') }}</th>
              <th class="monetary-table__subheader monetary-table__subheader--executed resource-table__colheader px-2 py-1.5 font-semibold">{{ $t('executed_accum') }}</th>
              <th class="monetary-table__subheader resource-table__colheader px-2 py-1.5 font-semibold">{{ $t('progress') }}</th>
              <th class="monetary-table__subheader monetary-table__subheader--pending resource-table__colheader px-2 py-1.5 font-semibold">{{ $t('remaining') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!isTableLoading && !tableDisplayRows.length">
              <td colspan="14" class="px-4 py-8 text-center text-grey-dark">
                {{ $t('no_results') }}
              </td>
            </tr>

            <tr
              v-for="row in tableDisplayRows"
              :key="row.key"
              class="border-b border-gray-200"
              :class="row.isProject ? 'monetary-table__row--project' : (row.depth === 0 ? 'monetary-table__row--parent' : 'monetary-table__row--child')"
              :style="rowAccentStyle(row)"
            >
              <td class="monetary-table__sticky-col px-3 py-2 text-grey-darkest" :class="row.depth > 0 ? 'text-xs' : ''">
                <div
                  class="flex items-center gap-2 min-w-0"
                  :style="rowIndentStyle(row)"
                  :class="row.hasChildren ? 'cursor-pointer select-none' : ''"
                  @click="row.hasChildren ? toggleGroupExpand(row) : null"
                >
                  <b-icon
                    v-if="isRowLoadingChildren(row)"
                    pack="fas"
                    icon="sync-alt"
                    size="is-small"
                    custom-class="fa-spin text-grey flex-shrink-0"
                  />
                  <b-icon
                    v-else-if="row.hasChildren"
                    pack="fas"
                    :icon="isGroupExpanded(row.key) ? 'chevron-down' : 'chevron-right'"
                    size="is-small"
                    class="text-grey flex-shrink-0"
                  />
                  <span v-else class="w-3 flex-shrink-0" />
                  <span :class="rowLabelClass(row)">{{ row.label }}</span>
                </div>
              </td>
              <td class="px-2 py-2 text-center text-grey-dark whitespace-nowrap">{{ row.unit }}</td>

              <!-- Plan (linea base) -->
              <td class="px-2 py-2 text-right font-semibold text-grey-darkest whitespace-nowrap">{{ formatAmount(row.plan.quantity) }}</td>
              <td class="px-2 py-2 text-right whitespace-nowrap">{{ formatAmount(row.executedPrev) }}</td>
              <td class="monetary-table__cell--executed px-2 py-2 text-right whitespace-nowrap">{{ formatAmount(row.executedPeriod) }}</td>
              <td class="monetary-table__cell--executed px-2 py-2 text-right whitespace-nowrap">{{ formatAmount(row.executedAccum) }}</td>
              <td class="px-2 py-2 text-right whitespace-nowrap">{{ formatPercent(row.plan.progressPct) }}</td>
              <td class="monetary-table__cell--pending px-2 py-2 text-right whitespace-nowrap">{{ formatSignedAmount(row.plan.remaining) }}</td>

              <!-- Actual (cantidad vigente) -->
              <td class="px-2 py-2 text-right font-semibold text-grey-darkest whitespace-nowrap">{{ formatAmount(row.actual.quantity) }}</td>
              <td class="px-2 py-2 text-right whitespace-nowrap">{{ formatAmount(row.executedPrev) }}</td>
              <td class="monetary-table__cell--executed px-2 py-2 text-right whitespace-nowrap">{{ formatAmount(row.executedPeriod) }}</td>
              <td class="monetary-table__cell--executed px-2 py-2 text-right whitespace-nowrap">{{ formatAmount(row.executedAccum) }}</td>
              <td class="px-2 py-2 text-right whitespace-nowrap">{{ formatPercent(row.actual.progressPct) }}</td>
              <td class="monetary-table__cell--pending px-2 py-2 text-right whitespace-nowrap">{{ formatSignedAmount(row.actual.remaining) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import ScaleLoader from 'vue-spinner/src/ScaleLoader.vue'
import dashboardPanelTreeMixin, { pct, sumField, appendVisibleRows } from './dashboard-panel-tree-mixin'

export default {
  name: 'DashboardSubpanelResourceExecutions',

  components: {
    ScaleLoader,
  },

  mixins: [dashboardPanelTreeMixin],

  computed: {
    nodeRows() {
      return (this.rootGroups || []).map((node) => this.buildRowFromNode(node, 0))
    },

    projectTotalsRow() {
      const roots = this.nodeRows
      if (!roots.length) {
        return null
      }

      return this.buildDerivedRow({
        key: 'project',
        id: 'project',
        label: this.$t('project').toUpperCase(),
        unit: this.currencySymbol,
        depth: 0,
        isProject: true,
        hasChildren: false,
        children: [],
        planQuantity: sumField(roots, (r) => r.plan.quantity),
        actualQuantity: sumField(roots, (r) => r.actual.quantity),
        executedPrev: sumField(roots, (r) => r.executedPrev),
        executedPeriod: sumField(roots, (r) => r.executedPeriod),
      })
    },

    tableDisplayRows() {
      const rows = []
      if (this.projectTotalsRow) {
        rows.push(this.projectTotalsRow)
      }
      this.nodeRows.forEach((node) => {
        appendVisibleRows(node, rows, (key) => this.isGroupExpanded(key))
      })
      return rows
    },
  },

  methods: {
    async fetchNodeChildren(parentId) {
      const { monthStart, periodEnd } = this.periodDates
      const params = {
        location_id: 0,
        status_id: 1,
        start: monthStart,
        end: periodEnd,
      }

      if (parentId !== null && parentId !== undefined) {
        params.parent_chapter_id = parentId
      }

      const url = `/api/v2/project/${this.projectId}/dashboard/resourceexecutions`
      const { data } = await axios.get(url, { params })
      const payload = data && data.data ? data.data : {}
      return Array.isArray(payload.nodes) ? payload.nodes : []
    },

    buildDerivedRow({
      key,
      id,
      label,
      unit,
      depth,
      isProject,
      hasChildren,
      children,
      planQuantity,
      actualQuantity,
      executedPrev,
      executedPeriod,
    }) {
      const executedAccum = executedPrev + executedPeriod

      return {
        key,
        id,
        label,
        unit,
        depth,
        isProject: Boolean(isProject),
        hasChildren: Boolean(hasChildren),
        children: children || [],
        executedPrev,
        executedPeriod,
        executedAccum,
        plan: {
          quantity: planQuantity,
          progressPct: pct(executedAccum, planQuantity),
          remaining: planQuantity - executedAccum,
        },
        actual: {
          quantity: actualQuantity,
          progressPct: pct(executedAccum, actualQuantity),
          remaining: actualQuantity - executedAccum,
        },
      }
    },

    buildRowFromNode(node, depth) {
      const isResource = node.node_type === 'resource'
      const executedPrev = this.progressMode === 'actual'
        ? Number(node.executed_prev_actual) || 0
        : Number(node.executed_prev) || 0
      const executedPeriod = this.progressMode === 'actual'
        ? Number(node.executed_period_actual) || 0
        : Number(node.executed_period) || 0

      const children = (this.childrenByParentId[node.id] || [])
        .map((child) => this.buildRowFromNode(child, depth + 1))

      return this.buildDerivedRow({
        key: `${node.node_type}-${node.id}`,
        // los recursos no se expanden; el prefijo evita colision con ids de capitulos en el estado de carga
        id: isResource ? `resource-${node.id}` : node.id,
        label: node.name || String(node.id),
        unit: isResource ? (node.unit || '—') : this.currencySymbol,
        depth,
        isProject: false,
        hasChildren: !isResource && Number(node.children_count || 0) > 0,
        children,
        planQuantity: Number(node.plan_total) || 0,
        actualQuantity: Number(node.actual_total) || 0,
        executedPrev,
        executedPeriod,
      })
    },
  },
}
</script>

<style scoped src="./monetary-panel.css"></style>

<style scoped>
/* Cabeceras de columna en varias lineas para no ensanchar columnas de numeros cortos */
.resource-table__colheader {
  white-space: normal;
  width: 6.5rem;
  text-align: center;
  vertical-align: middle;
}
</style>
